import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'

interface GoodbyeCardProps {
  title: string
  message: string
  language: string
  onUpdate: (data: { message: string; language: string }) => void
}

export function GoodbyeCard({ title, message, language, onUpdate }: GoodbyeCardProps) {
  const [editMode, setEditMode] = useState(false)
  const [editingText, setEditingText] = useState(message)
  const [localLanguage, setLocalLanguage] = useState(language || 'de')

  useEffect(() => {
    setEditingText(message)
  }, [message])

  useEffect(() => {
    setLocalLanguage(language || 'de')
  }, [language])

  const handleSave = () => {
    if (!editingText.trim()) return

    onUpdate({
      message: editingText,
      language: localLanguage,
    })

    setEditMode(false)
  }

  const handleCancel = () => {
    setEditingText(message)
    setLocalLanguage(language || 'de')
    setEditMode(false)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave()
    }
  }

  return (
    <div className="w-full">
      <Card className="bg-white rounded-lg shadow-sm border border-gray-100 w-full overflow-hidden">
        <CardHeader className="pb-3 pt-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium whitespace-nowrap text-xs text-gray-500">
              {title}
            </h3>
            {!editMode && (
              <Button
                variant="outline"
                size="sm"
                className="border-brand-primary text-brand-primary h-8 hover:bg-brand-primary/10"
                onClick={() => setEditMode(true)}
              >
                Bearbeiten
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="py-4 px-6">
          {!editMode ? (
            <div className="flex gap-2 items-center">
              <Badge 
                variant="secondary" 
                className="w-8 h-8 flex items-center justify-center bg-brand-primary/10 text-brand-primary font-medium"
              >
                {localLanguage.toUpperCase()}
              </Badge>
              <p className="text-gray-700 font-medium text-base">{editingText}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Goodbye Text */}
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-500">Verabschiedungstext</Label>
                <Input
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="w-full border-gray-200 focus-visible:ring-brand-primary focus-visible:border-brand-primary"
                  placeholder="Auf Wiedersehen..."
                />
              </div>

              {/* Language Select */}
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-500">Sprache</Label>
                <Select value={localLanguage} onValueChange={setLocalLanguage}>
                  <SelectTrigger className="w-full md:w-[200px] border-gray-200 focus:ring-brand-primary focus:border-brand-primary">
                    <SelectValue placeholder="Sprache wählen">
                      {localLanguage.toUpperCase()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Sprache</SelectLabel>
                      <SelectItem value="de">DE - Deutsch</SelectItem>
                      <SelectItem value="en">EN - English</SelectItem>
                      <SelectItem value="fr">FR - Français</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </CardContent>

        {editMode && (
          <CardFooter className="py-4 px-6 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              className="border-brand-primary text-brand-primary hover:bg-brand-primary/10"
              onClick={handleSave}
            >
              Speichern
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-500 hover:bg-gray-100"
              onClick={handleCancel}
            >
              Abbrechen
            </Button>
          </CardFooter>
        )}
      </Card>
    </div>
  )
}
